// frontend/src/features/bapb/components/SupportedCadsPanel.jsx
//
// Coverage panel for the Refresh-from-CAD info modal.
// Renders a real Leaflet map of Texas with all 254 county boundaries,
// supported counties filled with packet-navy. Fetches the GeoJSON from
// /geo/tx-counties.geojson (served from frontend/public/geo).
//
// Right-hand searchable, scrollable list stays synced with map hover state.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import L from "leaflet";
import { cartoTileUrl, CARTO_ATTRIBUTION } from "../../../lib/basemap";

// ---------- Per-CAD display + (optional) county name override ----------
// Slugs that don't directly match the GeoJSON county NAME need a `geoName`.
// (e.g. "fortbend" → "Fort Bend", "elpaso" → "El Paso")
const COUNTY_META = {
  dallas:     { display: "Dallas",     geoName: "Dallas"     },
  tarrant:    { display: "Tarrant",    geoName: "Tarrant"    },
  smith:      { display: "Smith",      geoName: "Smith"      },
  collin:     { display: "Collin",     geoName: "Collin"     },
  bexar:      { display: "Bexar",      geoName: "Bexar"      },
  brazoria:   { display: "Brazoria",   geoName: "Brazoria"   },
  denton:     { display: "Denton",     geoName: "Denton"     },
  travis:     { display: "Travis",     geoName: "Travis"     },
  rockwall:   { display: "Rockwall",   geoName: "Rockwall"   },
  montgomery: { display: "Montgomery", geoName: "Montgomery" },
  ellis:      { display: "Ellis",      geoName: "Ellis"      },
  mclennan:   { display: "McLennan",   geoName: "McLennan"   },
  potter:     { display: "Potter",     geoName: "Potter"     },
  harris:     { display: "Harris",     geoName: "Harris"     },
  hays:       { display: "Hays",       geoName: "Hays"       },
  comal:      { display: "Comal",      geoName: "Comal"      },
  lubbock:    { display: "Lubbock",    geoName: "Lubbock"    },
  webb:       { display: "Webb",       geoName: "Webb"       },
  jefferson:  { display: "Jefferson",  geoName: "Jefferson"  },
  gregg:      { display: "Gregg",      geoName: "Gregg"      },
  fortbend:   { display: "Fort Bend",  geoName: "Fort Bend"  },
  victoria:   { display: "Victoria",   geoName: "Victoria"   },
  cameron:    { display: "Cameron",    geoName: "Cameron"    },
  hidalgo:    { display: "Hidalgo",    geoName: "Hidalgo"    },
  randall:    { display: "Randall",    geoName: "Randall"    },
  elpaso:     { display: "El Paso",    geoName: "El Paso"    },
  kaufman:    { display: "Kaufman",    geoName: "Kaufman"    },
  wichita:    { display: "Wichita",    geoName: "Wichita"    },
  lamar:      { display: "Lamar",      geoName: "Lamar"      },
  wise:       { display: "Wise",       geoName: "Wise"       },
  wood:       { display: "Wood",       geoName: "Wood"       },
  hopkins:    { display: "Hopkins",    geoName: "Hopkins"    },
  bowie:      { display: "Bowie",      geoName: "Bowie"      },
  hunt:       { display: "Hunt",       geoName: "Hunt"       },
  grayson:    { display: "Grayson",    geoName: "Grayson"    },
};

const prettify = (key) =>
  COUNTY_META[key]?.display ||
  key.charAt(0).toUpperCase() + key.slice(1);

// Packet palette
const NAVY      = "#0E1B3D";
const ACCENT    = "#6E84C7";
const HOVER     = ACCENT; // packet accent blue on hover

// One-time CSS injection for the map container so tiles look muted/branded
const STYLE_ID = "supported-cads-map-style";
if (typeof document !== "undefined" && !document.head.querySelector(`[data-${STYLE_ID}]`)) {
  const el = document.createElement("style");
  el.setAttribute(`data-${STYLE_ID}`, "");
  el.textContent = `
    .supported-cads-map .leaflet-tile-pane { filter: saturate(0.25) brightness(1.05) contrast(0.95); }
    .supported-cads-map .leaflet-control-attribution { font-size: 9px; }
    .supported-cads-tooltip {
      background: #0E1B3D; color: #fff; border: none; border-radius: 6px;
      padding: 6px 10px; font-weight: 600; font-size: 12px;
      box-shadow: 0 4px 14px rgba(0,0,0,0.25);
    }
    .supported-cads-tooltip::before { border-top-color: #0E1B3D !important; }
    .supported-cads-tooltip.unsupported { background: #475569; }
    .supported-cads-tooltip.unsupported::before { border-top-color: #475569 !important; }
  `;
  document.head.appendChild(el);
}

// Texas bounding box (covers state with a touch of padding)
const TX_BOUNDS = L.latLngBounds(
  [25.6, -106.9],
  [36.7, -93.4]
);

export default function SupportedCadsPanel({
  supportedCads = [],
  className = "",
}) {
  const [query, setQuery] = useState("");
  const [hoverKey, setHoverKey] = useState(null);
  const [geo, setGeo] = useState(null);
  const [geoErr, setGeoErr] = useState(null);
  const layerRef = useRef(null);

  // Fetch the GeoJSON once
  useEffect(() => {
    let alive = true;
    fetch("/geo/tx-counties.geojson")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => { if (alive) setGeo(data); })
      .catch((err) => { if (alive) setGeoErr(err.message || String(err)); });
    return () => { alive = false; };
  }, []);

  // supported county-name set (lowercased) for fast lookup in the style fn
  const supportedNameSet = useMemo(() => {
    const s = new Set();
    for (const k of supportedCads) {
      const meta = COUNTY_META[k];
      const name = (meta?.geoName || k).toLowerCase();
      s.add(name);
    }
    return s;
  }, [supportedCads]);

  // map county-name (lowercase) → cad slug
  const nameToSlug = useMemo(() => {
    const m = new Map();
    for (const k of supportedCads) {
      const meta = COUNTY_META[k];
      const name = (meta?.geoName || k).toLowerCase();
      m.set(name, k);
    }
    return m;
  }, [supportedCads]);

  // searchable list
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...supportedCads]
      .map((k) => ({ key: k, name: prettify(k) }))
      .filter(
        (c) =>
          !q ||
          c.key.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [supportedCads, query]);

  // GeoJSON style — supported = navy fill, others = light gray
  const styleFor = (feature, isHover) => {
    const name = String(feature?.properties?.NAME || "").toLowerCase();
    const supported = supportedNameSet.has(name);
    return {
      fillColor:   isHover ? HOVER : (supported ? NAVY : "#e2e8f0"),
      fillOpacity: isHover ? 0.85 : (supported ? 0.78 : 0.55),
      color:       supported ? "#ffffff" : "#cbd5e1",
      weight:      isHover ? 2 : (supported ? 1 : 0.6),
      opacity:     1,
    };
  };

  // Re-style supported counties when hoverKey changes (from list-row hover)
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.eachLayer((l) => {
      const f = l.feature;
      const name = String(f?.properties?.NAME || "").toLowerCase();
      const slug = nameToSlug.get(name);
      l.setStyle(styleFor(f, !!slug && slug === hoverKey));
    });
  }, [hoverKey, supportedNameSet, nameToSlug]);

  const onEachFeature = (feature, leafletLayer) => {
    const name = String(feature?.properties?.NAME || "").toLowerCase();
    const display = feature?.properties?.NAME || "Unknown";
    const slug = nameToSlug.get(name);
    const supported = !!slug;

    const tooltipText = supported
      ? `${display} CAD &middot; supported`
      : `${display} County &middot; not yet supported`;
    leafletLayer.bindTooltip(tooltipText, {
      sticky: true,
      direction: "top",
      offset: [0, -4],
      className: `supported-cads-tooltip${supported ? "" : " unsupported"}`,
    });

    leafletLayer.on("mouseover", () => {
      if (slug) setHoverKey(slug);
      // brief style boost even on unsupported
      leafletLayer.setStyle(styleFor(feature, true));
    });
    leafletLayer.on("mouseout", () => {
      setHoverKey(null);
      leafletLayer.setStyle(styleFor(feature, false));
    });
  };

  // pan-fit Texas after layer is ready
  const onMapReady = (map) => {
    map.fitBounds(TX_BOUNDS, { padding: [20, 20] });
  };

  return (
    <div className={`flex flex-col md:flex-row h-full w-full overflow-hidden bg-white ${className}`}>
      {/* ───────── MAP ───────── */}
      <div className="flex-1 relative bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 min-h-[320px]">
        {geoErr ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-red-600 p-4 text-center">
            Failed to load county boundaries: {geoErr}
          </div>
        ) : !geo ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
            Loading Texas county map…
          </div>
        ) : (
          <MapContainer
            bounds={TX_BOUNDS}
            scrollWheelZoom={false}
            zoomControl={true}
            attributionControl={true}
            style={{ height: "100%", width: "100%", background: "#f1f5f9" }}
            className="supported-cads-map"
            whenReady={(e) => onMapReady(e.target)}
          >
            <TileLayer
              attribution={CARTO_ATTRIBUTION}
              url={cartoTileUrl("light_all")}
            />
            <GeoJSON
              data={geo}
              style={(f) => styleFor(f, false)}
              onEachFeature={onEachFeature}
              ref={(r) => { layerRef.current = r; }}
            />
          </MapContainer>
        )}

        {/* Legend (overlays the map) */}
        <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs space-y-1 pointer-events-none">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: NAVY, opacity: 0.78 }} />
            <span className="text-slate-700 font-medium">Supported CAD</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm bg-slate-200 border border-slate-300" />
            <span className="text-slate-700">Not yet supported</span>
          </div>
        </div>

        {/* Coverage badge */}
        <div className="absolute top-3 right-3 z-[400] bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-sm font-semibold text-emerald-800 shadow-sm pointer-events-none">
          {supportedCads.length} of 254 counties
        </div>
      </div>

      {/* ───────── LIST ───────── */}
      <aside className="md:w-[320px] w-full flex flex-col bg-white">
        <div className="p-4 border-b border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Coverage list
          </div>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search counties…"
              className="w-full text-sm border border-slate-300 rounded-md pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
            />
            <svg
              className="w-4 h-4 absolute left-3 top-2.5 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M16 10.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z" />
            </svg>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Showing {list.length} of {supportedCads.length}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.length === 0 ? (
            <div className="text-sm text-slate-500 px-4 py-3">
              No CADs match "{query}".
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.map((c) => (
                <li
                  key={c.key}
                  onMouseEnter={() => setHoverKey(c.key)}
                  onMouseLeave={() => setHoverKey(null)}
                  className={`px-4 py-2 text-sm flex items-center justify-between cursor-default transition-colors ${
                    hoverKey === c.key
                      ? "bg-[#EDF3FF] text-[#0E1B3D]"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm"
                      style={{
                        backgroundColor: hoverKey === c.key ? HOVER : NAVY,
                      }}
                    />
                    {c.name} CAD
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                    {c.key}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
